import { useModalContext } from 'context/EventModalContext'
import React, { useCallback } from 'react'
import Button from 'components/Button'
import { EventQuery, EventRegistrationsQuery } from 'graphql/documents'
import {
  useSetAttendeeRoleMutation,
  EventRegistrationRole,
  EventState,
  EventRegistrationsQueryResult,
  useBanMemberFromEventsMutation
} from 'graphql/generated'
import { useQueryErrorHandler } from 'hooks'
import { useConfirmPromptModal } from 'components/modals/ConfirmPromptModal'
import { useEventContext } from 'context/EventContext'
import language from 'services/Language'

interface IProps {
  attendee: EventRegistrationsQueryResult['event']['registrations']['edges'][0]['node']
}

function RSVPListItemControls({ attendee }: IProps) {
  const { event } = useEventContext()

  const { hideModal } = useModalContext()

  const onError = useQueryErrorHandler(language.events.make_host_fail)

  const [setRole] = useSetAttendeeRoleMutation({
    refetchQueries: (args: any) =>
      args?.data.setAttendeeRole.role === 'HOST'
        ? [
            {
              query: EventQuery,
              variables: {
                id: event.id
              }
            },
            {
              query: EventRegistrationsQuery,
              variables: {
                id: event.id,
                registrationConnection: {
                  first: 100
                }
              }
            }
          ]
        : [],
    onError,
    onCompleted: hideModal
  })

  const canSetRole =
    // the host's role can't be changed directly
    attendee.user.id !== event.user.id &&
    // event must not have started yet
    event.state === EventState.SCHEDULED &&
    // attendee must not be waitlisted
    !attendee.waitlisted

  const confirm = useConfirmPromptModal()

  const makeHost = useCallback(
    () =>
      confirm({
        title: language.events.make_host_title,
        body: language.events.make_host_warning,
        cancelLabel: language.generic.cancel,
        confirmLabel: language.generic.apply,
        onClick: () =>
          setRole({
            variables: {
              eventId: event.id,
              userId: attendee.user.id,
              role: EventRegistrationRole.HOST
            }
          })
      }),
    [attendee.user.id, event.id, confirm, setRole]
  )

  const onBanError = useQueryErrorHandler('Failed to ban user')
  const [banMember] = useBanMemberFromEventsMutation({
    variables: {
      collectiveId: event.collective?.id || '0',
      userId: attendee.user.id // This should be the collective user id
    },
    onError: onBanError,
    onCompleted: () => hideModal()
  })

  if (!event.isPrivileged || !canSetRole) {
    return null
  }

  return (
    <div className="flex flex-row gap-10">
      {[
        attendee.role === EventRegistrationRole.GUEST && {
          icon: 'user-chart',
          label: language.events.make_speaker,
          onClick: () =>
            setRole({
              variables: {
                eventId: event.id,
                userId: attendee.user.id,
                role: EventRegistrationRole.SPEAKER
              }
            })
        },
        attendee.role === EventRegistrationRole.SPEAKER && {
          icon: 'stop',
          label: language.events.remove_speaker,
          onClick: () =>
            setRole({
              variables: {
                eventId: event.id,
                userId: attendee.user.id,
                role: EventRegistrationRole.GUEST
              }
            })
        },
        {
          icon: 'crown',
          label: language.events.make_host_title,
          onClick: makeHost
        },
        {
          icon: 'user-x-mark',
          label: 'Ban from All Events',
          onClick: banMember
        }
      ].map(v => {
        if (!v) {
          return null
        }

        return (
          <Button
            key={v.label}
            size={'xs'}
            color={'gray'}
            label={v.label}
            onClick={e => {
              e.preventDefault()
              v.onClick()
            }}
          />
        )
      })}
    </div>
  )
}

export default RSVPListItemControls
